import React from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { LoadingComponent } from 'recupero-comunes'
import KpiCard from '../dashboard-comunes/kpi-card'
import AvisoExportacion from '../dashboard-comunes/aviso-exportacion'
import DonutsPendientesCia from '../dashboard-comunes/donuts-pendientes-cia'
import MontosPorCiaBarras from '../dashboard-comunes/montos-por-cia-barras'
import EstEvalTabla from './est-eval-tabla'
import { formatMonto, formatFecha } from '../../common/dashboard-utils'
import { useExportarCasos } from '../../common/use-exportar-casos'
import * as estilos from '../../common/dashboard-estilos'

/**
 * Cuerpo del tablero Inter Compañía.
 *
 * El rediseño (V1.1, 04-09-2026) separa dos lecturas que antes estaban mezcladas:
 *    🟠 *Situación actual* — lo que hay HOY, sin filtro de período (Pendiente de Gestión y
 *       Aprobado Pendiente de Emisión, más los dos donuts por compañía).
 *    🔵 *Actividad del período* — lo que ocurrió dentro del rango de fechas (Cobrado, Pagado,
 *       Rechazado, las barras de pagado por compañía y la tabla de motivos).
 *    🚫 Mover un KPI de un bloque al otro cambia su significado de negocio: los del bloque ámbar
 *       NO se filtran por período ni deben hacerlo.
 * 🔴 Drop 2026-09-22: las 5 etiquetas de KPI se renombraron a "Cartas..." y el período se muestra
 *    como fecha exacta (`fechaDesde`/`fechaHasta`), ya no como rango de meses.
 */

const GroupHeader = ({ titulo, subtitulo, color }) => (
    <div style={estilos.grupoHeader(color)}>
        <span style={estilos.grupoTitulo(color)}>{titulo}</span>
        {subtitulo && <span style={estilos.grupoSubtitulo}>{subtitulo}</span>}
    </div>
)

const ResumenGeneral = () => {
    const resumen = useSelector((state) => state.dashboardInterciaReducer.resumen)
    const filtros = useSelector((state) => state.dashboardInterciaReducer.filtros)

    // RG-602: cada tarjeta, porción, barra y fila de la tabla descarga los casos detrás de su número.
    const { exportar, exportando, aviso, cierraAviso } = useExportarCasos('intercia')
    const exportaConcepto = (concepto) => () => exportar(concepto)

    // Una porción o barra sin compañía (`companiaId` nulo) no se puede acotar: se avisa en vez de
    // exportar todo el conjunto como si fuera esa porción.
    const exportaPorCia = (concepto) => (porcion) => {
        if (!porcion?.companiaId) return
        exportar(concepto, { ciaId: porcion.companiaId })
    }

    const kpis = resumen?.kpis || {}
    const porCiaPendientes = resumen?.porCiaPendientes || []
    const porCiaPendienteEmision = resumen?.porCiaPendienteEmision || []
    const porCiaPagado = resumen?.porCiaPagado || []
    const porMotivoRespuesta = resumen?.porMotivoRespuesta || []

    const rangoPeriodo = filtros.fechaDesde && filtros.fechaHasta
        ? `${formatFecha(filtros.fechaDesde)} → ${formatFecha(filtros.fechaHasta)}`
        : ''

    return (
        <div>
            <LoadingComponent show={exportando} />
            <AvisoExportacion aviso={aviso} onClose={cierraAviso} />
            <Row className='g-3 mb-3'>

                {/* 🟠 Situación actual — independiente del período */}
                <Col xs={12} xl={5}>
                    <div style={estilos.grupoActual} className='d-flex flex-column'>
                        <GroupHeader
                            titulo='Situación actual'
                            subtitulo='sin filtro de período'
                            color={estilos.COLOR_GRUPO_ACTUAL}
                        />
                        <Row className='g-2 mb-2'>
                            <Col xs={6}>
                                <KpiCard
                                    label='Cartas Pendientes de Gestión'
                                    value={formatMonto(kpis.montoPendienteGestion)}
                                    subLabel={kpis.casosAsignados ? `${kpis.casosAsignados} casos asignados` : undefined}
                                    color='#fd7e14'
                                    onExportar={exportaConcepto('pendienteGestion')}
                                    exportando={exportando}
                                />
                            </Col>
                            <Col xs={6}>
                                <KpiCard
                                    label='Cartas Aprobadas Pendiente de Emisión'
                                    value={formatMonto(kpis.montoPendiente)}
                                    subLabel={kpis.casosPendientes ? `${kpis.casosPendientes} casos` : undefined}
                                    color='#ffc107'
                                    onExportar={exportaConcepto('pendienteEmision')}
                                    exportando={exportando}
                                />
                            </Col>
                        </Row>
                        <Card className='flex-grow-1'>
                            <Card.Header as='h6' className='text-center' style={estilos.headerCard}>
                                Distribución por Compañía Tercero
                            </Card.Header>
                            <Card.Body style={estilos.chartContainer}>
                                <DonutsPendientesCia
                                    dataGestion={porCiaPendientes}
                                    dataEmision={porCiaPendienteEmision}
                                    onExportarGestion={exportaPorCia('pendienteGestion')}
                                    onExportarEmision={exportaPorCia('pendienteEmision')}
                                />
                            </Card.Body>
                        </Card>
                    </div>
                </Col>

                {/* 🔵 Actividad del período — dentro del rango de meses */}
                <Col xs={12} xl={7}>
                    <div style={estilos.grupoPeriodo} className='d-flex flex-column'>
                        <GroupHeader
                            titulo='Actividad del período'
                            subtitulo={rangoPeriodo}
                            color={estilos.COLOR_GRUPO_PERIODO}
                        />
                        <Row className='g-2 mb-2'>
                            <Col xs={4}>
                                <KpiCard
                                    label='Cartas Cobradas'
                                    value={formatMonto(kpis.totalMontoPagoCia)}
                                    subLabel={kpis.totalCasos ? `${kpis.totalCasos} casos` : undefined}
                                    color='#007bff'
                                    onExportar={exportaConcepto('cobradas')}
                                    exportando={exportando}
                                />
                            </Col>
                            <Col xs={4}>
                                <KpiCard
                                    label='Cartas Pagadas'
                                    value={formatMonto(kpis.montoPagado)}
                                    subLabel={kpis.casosPagados ? `${kpis.casosPagados} casos` : undefined}
                                    color='#28a745'
                                    onExportar={exportaConcepto('pagadas')}
                                    exportando={exportando}
                                />
                            </Col>
                            <Col xs={4}>
                                <KpiCard
                                    label='Cartas Rechazadas'
                                    value={formatMonto(kpis.montoRechazado)}
                                    subLabel={kpis.casosRechazados ? `${kpis.casosRechazados} casos` : undefined}
                                    color='#dc3545'
                                    onExportar={exportaConcepto('rechazadas')}
                                    exportando={exportando}
                                />
                            </Col>
                        </Row>
                        <Card className='flex-grow-1'>
                            <Card.Header as='h6' className='text-center' style={estilos.headerCard}>
                                Monto Pagado y Emitido por Compañía Tercero
                            </Card.Header>
                            <Card.Body style={estilos.chartContainer}>
                                <MontosPorCiaBarras data={porCiaPagado} onExportar={exportaPorCia('pagadas')} />
                            </Card.Body>
                        </Card>
                    </div>
                </Col>
            </Row>

            {/* La tabla de motivos también es actividad del período: lleva el mismo encabezado azul. */}
            <Row className='g-3 mb-3'>
                <Col xs={12}>
                    <div style={estilos.grupoPeriodo}>
                        <GroupHeader
                            titulo='Actividad del período'
                            subtitulo={rangoPeriodo}
                            color={estilos.COLOR_GRUPO_PERIODO}
                        />
                        <Card>
                            <Card.Header as='h6' className='text-center' style={estilos.headerCard}>
                                Estado Evaluación CIA
                            </Card.Header>
                            <Card.Body className='p-0'>
                                <EstEvalTabla
                                    data={porMotivoRespuesta}
                                    onExportar={(fila) => exportar('motivoRespuesta', { motivoId: fila.motivoId ?? null })}
                                />
                            </Card.Body>
                        </Card>
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export default ResumenGeneral
